/* ============================================================================
   AUDITORÍA DE SEGURIDAD — integridad bow-tie y estado de registro.

   Dos fuentes, con alcances distintos:

   1. Anexo 2 de la Eurocopter IN 2255-I-00: la ficha de seguimiento de cada
      Evento Indeseable —precursores, defensas de control y recuperación,
      medidas de protección, responsable y estado de implementación.

   2. Metodología bow-tie descrita por SKYbrary/EUROCONTROL
      (https://skybrary.aero/articles/bow-tie-risk-management-methodology).
      Aporta dos cosas que el Anexo 2 no pide explícitamente y que acá se
      miden:

        · El PELIGRO es distinto del evento temido. El bow-tie empieza en el
          peligro; el evento temido es el momento en que se pierde su control.
          Registrar el evento sin nombrar el peligro deja el diagrama sin
          nudo izquierdo.

        · Los FACTORES DE ESCALADA. Dos de las ocho preguntas de la
          metodología son "¿cómo podrían fallar los controles?" y "¿cómo nos
          aseguramos de que no fallen?". Una barrera declarada que nadie
          verifica no es una barrera: es una suposición.

   ADVERTENCIA SOBRE BARS
   ----------------------
   El registro BARS lo emite la BARS Program Office a partir de auditorías
   hechas por auditores acreditados de una Compañía de Auditoría registrada.
   NINGUNA aplicación puede otorgar, renovar ni simular esa designación.

   Lo que se calcula acá es una AUTOEVALUACIÓN INTERNA que reproduce la lógica
   de colores del programa para que el operador vea cómo llegaría a una
   auditoría real. No es un registro, no vale como evidencia ante la BPO, y la
   interfaz tiene que decirlo en cada pantalla donde aparezca.
   ========================================================================== */

/**
 * Prioridades de no conformidad del programa BARS.
 *
 * Los PLAZOS de cierre NO están acá. Los fija el informe de auditoría de cada
 * operador y no los tengo: inventarlos produciría un semáforo que dice "en
 * plazo" contra una fecha que nadie estableció. Cada no conformidad lleva su
 * propia fecha límite, cargada de su informe.
 */
export const PRIORIDADES_NC = [
  { clave: "P1", etiqueta: "Prioridad 1", descripcion: "Afecta el registro. Debe cerrarse para obtener o mantener la designación." },
  { clave: "P2", etiqueta: "Prioridad 2", descripcion: "Afecta el registro. Debe cerrarse para obtener o mantener la designación." },
  { clave: "P3", etiqueta: "Prioridad 3", descripcion: "Observación de mejora. No condiciona la designación del registro." },
];

/** Solo P1 y P2 condicionan el color del registro. */
export const PRIORIDADES_QUE_CONDICIONAN = ["P1", "P2"];

/**
 * Estado de una no conformidad respecto de su plazo.
 *
 * @param {{prioridad:string, cerradaEl:string|null, plazoHasta:string|null}} nc
 * @param {Date} hoy
 */
export function estadoNoConformidad(nc, hoy = new Date()) {
  const limite = nc.plazoHasta ? new Date(nc.plazoHasta) : null;
  const cierre = nc.cerradaEl ? new Date(nc.cerradaEl) : null;

  if (cierre) {
    if (!limite) return { abierta: false, enPlazo: null, etiqueta: "Cerrada (sin plazo registrado)" };
    return cierre <= limite
      ? { abierta: false, enPlazo: true, etiqueta: "Cerrada en plazo" }
      : { abierta: false, enPlazo: false, etiqueta: "Cerrada fuera de plazo" };
  }

  if (!limite) return { abierta: true, enPlazo: null, etiqueta: "Abierta, sin plazo registrado" };
  return hoy <= limite
    ? { abierta: true, enPlazo: true, etiqueta: "Abierta, dentro del plazo" }
    : { abierta: true, enPlazo: false, etiqueta: "Abierta, plazo vencido" };
}

/**
 * Color de registro según la lógica del programa BARS.
 *
 *   VERDE  — auditoría realizada, todas las P1 y P2 cerradas.
 *   PLATA  — dos auditorías; la de renovación se hizo antes del vencimiento
 *            del registro vigente y las P1/P2 se cerraron dentro del plazo.
 *   ORO    — dos o más renovaciones antes del vencimiento, con P1/P2 cerradas
 *            dentro de los plazos asignados.
 *   ÁMBAR  — auditoría realizada y no conformidades todavía abiertas dentro
 *            del plazo asignado. Es lo normal durante un ciclo de renovación.
 *
 * El estado FUERA DE PLAZO no figura en la descripción del programa: lo agrego
 * porque una P1 abierta con el plazo vencido no puede mostrarse del mismo
 * color que una abierta y en término. Queda marcado como criterio propio de la
 * app, no del programa.
 *
 * @param {{auditorias: Array, noConformidades: Array}} datos
 */
export function clasificarRegistro({ auditorias = [], noConformidades = [] } = {}, hoy = new Date()) {
  if (auditorias.length === 0) {
    return {
      color: "SIN AUDITORÍA", propio: false,
      motivo: "Todavía no se registró ninguna auditoría.",
    };
  }

  const condicionantes = noConformidades.filter((nc) => PRIORIDADES_QUE_CONDICIONAN.includes(nc.prioridad));
  const estados = condicionantes.map((nc) => ({ nc, ...estadoNoConformidad(nc, hoy) }));

  const abiertasVencidas = estados.filter((e) => e.abierta && e.enPlazo === false);
  if (abiertasVencidas.length > 0) {
    return {
      color: "FUERA DE PLAZO", propio: true,
      motivo: `${abiertasVencidas.length} no conformidad(es) P1/P2 abiertas con el plazo vencido.`,
    };
  }

  const abiertas = estados.filter((e) => e.abierta);
  if (abiertas.length > 0) {
    return {
      color: "ÁMBAR", propio: false,
      motivo: `${abiertas.length} no conformidad(es) P1/P2 abiertas, dentro del plazo asignado. Es lo normal durante un ciclo de renovación.`,
    };
  }

  // Todas las P1/P2 cerradas. El nivel depende de las renovaciones.
  const cerradasFueraDePlazo = estados.filter((e) => e.enPlazo === false).length;
  // Boolean() y no `=== true`: el dato viene de una casilla, de IndexedDB o de
  // una importación, y puede llegar como 1 o "1". Con comparación estricta un
  // operador que hizo todo bien perdía la designación en silencio. El error
  // caía del lado conservador, que es el correcto, pero seguía siendo un error.
  const renovacionesEnTermino = auditorias.filter((a) => a.tipo === "renovacion" && Boolean(a.antesDelVencimiento)).length;

  if (cerradasFueraDePlazo === 0 && renovacionesEnTermino >= 2) {
    return { color: "ORO", propio: false, motivo: `${renovacionesEnTermino} renovaciones antes del vencimiento, con todas las P1/P2 cerradas en plazo.` };
  }
  if (cerradasFueraDePlazo === 0 && renovacionesEnTermino === 1 && auditorias.length >= 2) {
    return { color: "PLATA", propio: false, motivo: "Renovación realizada antes del vencimiento, con las P1/P2 cerradas en plazo." };
  }
  return {
    color: "VERDE", propio: false,
    motivo: cerradasFueraDePlazo > 0
      ? `Todas las P1/P2 están cerradas, pero ${cerradasFueraDePlazo} se cerraron fuera de plazo: no acumula hacia plata u oro.`
      : "Auditoría realizada y todas las no conformidades P1 y P2 cerradas.",
  };
}

/* ────────────────────────── Integridad del bow-tie ──────────────────────── */

/** Cuenta líneas con contenido en un campo de texto libre. */
function lineas(texto) {
  if (typeof texto !== "string") return [];
  return texto.split(/\n|;/).map((s) => s.trim()).filter(Boolean);
}

/**
 * Mide qué tan completo está el bow-tie de un evento.
 *
 * Las ocho preguntas de la metodología, traducidas a comprobaciones sobre la
 * ficha del Anexo 2. Devuelve los huecos, no un puntaje bonito: el objetivo es
 * que el auditor vea qué falta, no que el operador se sienta bien.
 */
export function evaluarBowTie(evento = {}) {
  const amenazas = lineas(evento.precursores);
  const escalada = lineas(evento.factoresEscalada);

  const comprobaciones = [
    { clave: "peligro", pregunta: "¿Cuál es el peligro?", cumple: Boolean((evento.peligro || "").trim()) },
    { clave: "evento", pregunta: "¿Qué pasa cuando se pierde el control del peligro?", cumple: Boolean((evento.descripcion || "").trim()) },
    { clave: "amenazas", pregunta: "¿Qué evento podría liberar el peligro?", cumple: amenazas.length > 0 },
    { clave: "consecuencias", pregunta: "¿Cuáles son las consecuencias posibles?", cumple: (evento.euCategorias || []).length > 0 },
    { clave: "control", pregunta: "¿Cómo evitamos el evento?", cumple: Boolean((evento.medidasControl || "").trim()) },
    { clave: "recuperacion", pregunta: "¿Cómo recuperamos si el evento ocurre?", cumple: Boolean((evento.medidasRecuperacion || "").trim()) },
    { clave: "mitigacion", pregunta: "¿Cómo limitamos la gravedad de las consecuencias?", cumple: Boolean((evento.medidasMitigacion || "").trim()) },
    { clave: "escalada", pregunta: "¿Cómo podrían fallar los controles?", cumple: escalada.length > 0 },
    { clave: "sostenimiento", pregunta: "¿Cómo nos aseguramos de que los controles no fallen?", cumple: Boolean((evento.controlEscalada || "").trim()) },
    { clave: "responsable", pregunta: "¿Quién responde por las medidas?", cumple: Boolean((evento.responsable || "").trim()) },
  ];

  const cumplidas = comprobaciones.filter((c) => c.cumple).length;
  return {
    comprobaciones,
    faltantes: comprobaciones.filter((c) => !c.cumple),
    cumplidas,
    total: comprobaciones.length,
    porcentaje: Math.round((cumplidas / comprobaciones.length) * 100),
    amenazas: amenazas.length,
    factoresEscalada: escalada.length,
    // Una barrera declarada que nadie verifica no es una barrera. Si hay
    // medidas pero no hay nada que sostenga esas medidas, se marca.
    barrerasSinSostenimiento:
      Boolean((evento.medidasControl || "").trim()) && !(evento.controlEscalada || "").trim(),
  };
}

/**
 * Resumen de una cartera de eventos, para el panel de auditoría.
 */
export function resumenBowTie(eventos = []) {
  if (eventos.length === 0) {
    return { eventos: 0, promedio: 0, completos: 0, sinPeligro: 0, sinEscalada: 0, sinResponsable: 0, sinImplementar: 0 };
  }
  const evaluados = eventos.map((e) => ({ evento: e, bt: evaluarBowTie(e) }));
  return {
    eventos: eventos.length,
    promedio: Math.round(evaluados.reduce((s, x) => s + x.bt.porcentaje, 0) / eventos.length),
    completos: evaluados.filter((x) => x.bt.porcentaje === 100).length,
    sinPeligro: evaluados.filter((x) => !x.bt.comprobaciones.find((c) => c.clave === "peligro").cumple).length,
    sinEscalada: evaluados.filter((x) => x.bt.factoresEscalada === 0).length,
    sinResponsable: evaluados.filter((x) => !x.bt.comprobaciones.find((c) => c.clave === "responsable").cumple).length,
    sinImplementar: eventos.filter((e) => e.implementado === "No").length,
    detalle: evaluados,
  };
}
